/**
 * F-178 phase 2: the public embed of a show or an episode (`/embed/episode/{id}`, `/embed/show/{id}`),
 * framed by any site. Public, no session, no app shell; `server.js` opens `frame-ancestors` only here.
 * `?size=compact` (the default for an episode) or `full`.
 */
export type MediaEmbedTarget = { kind: 'episode' | 'show'; id: string }

const EMBED_PATH = /^\/embed\/(episode|show)\/([^/?#]+)\/?$/

export const isMediaEmbedPath = (path: string): boolean => EMBED_PATH.test(path)

export const mediaEmbedTargetOf = (path: string): MediaEmbedTarget | null => {
  const match = path.match(EMBED_PATH)
  if (!match) return null
  return { kind: match[1] as MediaEmbedTarget['kind'], id: decodeURIComponent(match[2]) }
}

export type MediaEmbedSize = 'compact' | 'full'

/** The iframe's suggested height for each size (the video one keeps 16:9 instead). */
export const EMBED_HEIGHTS: Record<'episode' | 'show', Record<MediaEmbedSize, number>> = {
  episode: { compact: 152, full: 232 },
  show: { compact: 420, full: 420 }
}

/** The `<iframe>` another site pastes. `origin` is the community's own address. */
export const mediaEmbedCode = ({ origin, kind, id, size, title, video }: {
  origin: string
  kind: 'episode' | 'show'
  id: string
  size: MediaEmbedSize
  title: string
  video?: boolean
}): string => {
  const src = `${origin.replace(/\/$/, '')}/embed/${kind}/${encodeURIComponent(id)}${kind === 'episode' && !video ? `?size=${size}` : ''}`
  const safeTitle = title.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
  const sizing = video
    ? 'width="100%" style="aspect-ratio:16/9;border:0;border-radius:12px"'
    : `width="100%" height="${EMBED_HEIGHTS[kind][size]}" style="border:0;border-radius:12px"`
  return `<iframe src="${src}" ${sizing} title="${safeTitle}" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" loading="lazy"></iframe>`
}
